import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Divider, Typography } from '@mui/material';

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '312362298768-fdrmn3lh4octns9b11eh3412gbjnr95q.apps.googleusercontent.com';

let googleScriptPromise = null;

function loadGoogleScript() {
  if (typeof window !== 'undefined' && window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (googleScriptPromise) {
    return googleScriptPromise;
  }

  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-identity="true"]');
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google sign-in.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.dataset.googleIdentity = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google sign-in.'));
    document.head.appendChild(script);
  });

  return googleScriptPromise;
}

function GoogleSignInButton({ text = 'signin_with', onCredential, helperText, disabled = false, disabledMessage = '' }) {
  const containerRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function renderButton() {
      if (!GOOGLE_CLIENT_ID) {
        setError('Google sign-in is not configured for this build.');
        return;
      }

      if (disabled) {
        setError('');
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
        return;
      }

      try {
        await loadGoogleScript();
        if (cancelled || !containerRef.current || !window.google?.accounts?.id) {
          return;
        }

        setError('');
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          ux_mode: 'popup',
          callback: (response) => {
            if (response?.credential) {
              onCredential(response.credential);
            }
          },
        });

        containerRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(containerRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text,
          width: 360,
        });
      } catch (scriptError) {
        if (!cancelled) {
          setError(scriptError.message || 'Unable to load Google sign-in.');
        }
      }
    }

    renderButton();
    return () => {
      cancelled = true;
    };
  }, [disabled, onCredential, text]);

  return (
    <Box>
      <Divider sx={{ my: 1.5 }}>
        <Typography sx={{ color: '#718792', fontSize: '0.85rem', fontWeight: 700 }}>or</Typography>
      </Divider>
      {disabled ? (
        <Box
          sx={{
            minHeight: 44,
            borderRadius: '12px',
            border: '1px solid rgba(15,61,86,0.14)',
            backgroundColor: 'rgba(15,61,86,0.04)',
            color: '#7a8d97',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
          }}
        >
          Continue with Google
        </Box>
      ) : (
        <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />
      )}
      {helperText && (
        <Typography sx={{ mt: 1.2, color: '#6c848f', fontSize: '0.92rem', textAlign: 'center' }}>
          {helperText}
        </Typography>
      )}
      {disabled && disabledMessage && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {disabledMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export default GoogleSignInButton;
